/// <reference types="google.maps" />
import { useCallback, useEffect, useMemo, useState, type ComponentProps } from 'react'
import {
  GoogleMap,
  GoogleMarkerClusterer,
  InfoWindowF,
  MarkerF,
  FLOAT_PANE,
  OverlayViewF,
} from '@react-google-maps/api'

import { scopedTranslation } from '@utils/I18n'

import type { LinkTarget } from '@utils/embeds/embedConfig'
import { PIN_PATH, PIN_PATH_HEIGHT, type MapPin } from '@utils/embeds/mapPins'
import { useGoogleMaps, useMapFailure } from '@utils/embeds/places'
import { embedTheme } from '@utils/embeds/theme'
import { EventSummary, PinCallout } from './EventSummary'

const t = scopedTranslation('embeds.events')

const MAP_OPTIONS: google.maps.MapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
}

const PHONE_MAP_OPTIONS: google.maps.MapOptions = { ...MAP_OPTIONS, cameraControl: false, zoomControl: true }

const SINGLE_PIN_ZOOM = 14

const RAISED_PIN_HEIGHT = 44
const RAISED_PIN_SCALE = 1.5
const CALLOUT_GAP = 6
const CALLOUT_MARGIN = 12
const CALLOUT_SETTLE_FRAMES = 10

type EventsMapProps = {
  pins: MapPin[]
  pinColor: string
  highlightedId: number | null
  selectedId: number | null
  onSelect: (id: number | null) => void
  links: LinkTarget
  rsvpLabel: string
  showCoverage: boolean
  locale: string
  apiKey: string
  country: string
  onError: () => void
  phone?: boolean
}

function pinIcon({ pinColor, raised }: { pinColor: string; raised: boolean }): google.maps.Symbol {
  return {
    path: PIN_PATH,
    fillColor: pinColor,
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 2,
    scale: raised ? RAISED_PIN_SCALE : 1.1,
  }
}

function clusterRenderer(pinColor: string) {
  return {
    render: ({ count, position }: { count: number; position: google.maps.LatLng }) =>
      new google.maps.Marker({
        position,
        title: t('map.cluster', { count }),
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        label: { text: String(count), color: embedTheme(pinColor).brandText, fontSize: '13px', fontWeight: '600' },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: pinColor,
          fillOpacity: 0.9,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          scale: 16,
        },
      }),
  }
}

export function EventsMap({
  pins,
  pinColor,
  highlightedId,
  selectedId,
  onSelect,
  links,
  rsvpLabel,
  showCoverage,
  locale,
  apiKey,
  country,
  onError,
  phone = false,
}: EventsMapProps) {
  const { isLoaded, loadError } = useGoogleMaps({ apiKey, country })
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [popupHeader] = useState(() => document.createElement('div'))
  const [tappedId, setTappedId] = useState<number | null>(null)
  const clustererOptions = useMemo(
    () => (isLoaded ? { renderer: clusterRenderer(pinColor) } : null),
    [isLoaded, pinColor]
  )
  const selected = pins.find(({ event }) => event.id === selectedId) ?? null
  const pinKey = pins.map(({ event, position }) => `${event.id}:${position.lat},${position.lng}`).join('|')

  useMapFailure({ loadError, onError })

  useEffect(() => {
    if (!map || pins.length === 0) return
    if (pins.length === 1) {
      map.setCenter(pins[0].position)
      map.setZoom(SINGLE_PIN_ZOOM)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    pins.forEach(({ position }) => bounds.extend(position))
    map.fitBounds(bounds, 48)
    google.maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() ?? 0) > SINGLE_PIN_ZOOM) map.setZoom(SINGLE_PIN_ZOOM)
    })
    // Pins are compared by pinKey, so a new array with the same pins keeps the supporter's pan and zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pinKey])

  const selectedLat = selected?.position.lat
  const selectedLng = selected?.position.lng
  useEffect(() => {
    if (!map || selectedLat === undefined || selectedLng === undefined) return
    const position = { lat: selectedLat, lng: selectedLng }
    if (!map.getBounds()?.contains(position)) map.panTo(position)
  }, [map, selectedLat, selectedLng])

  const settleCallout = useCallback(
    (callout: HTMLDivElement | null) => {
      if (!callout || !map) return
      google.maps.OverlayView.preventMapHitsAndGesturesFrom(callout)
      let lastLeft: number | null = null
      let framesLeft = CALLOUT_SETTLE_FRAMES
      const settle = () => {
        const box = callout.getBoundingClientRect()
        if (box.left !== lastLeft && --framesLeft > 0) {
          lastLeft = box.left
          requestAnimationFrame(settle)
          return
        }
        callout.querySelector('a')?.focus({ preventScroll: true })
        const frame = map.getDiv().getBoundingClientRect()
        const dx =
          box.left < frame.left + CALLOUT_MARGIN
            ? box.left - frame.left - CALLOUT_MARGIN
            : box.right > frame.right - CALLOUT_MARGIN
              ? box.right - frame.right + CALLOUT_MARGIN
              : 0
        const dy = box.top < frame.top + CALLOUT_MARGIN ? box.top - frame.top - CALLOUT_MARGIN : 0
        if (dx || dy) map.panBy(dx, dy)
      }
      requestAnimationFrame(settle)
    },
    [map]
  )

  if (!isLoaded || !clustererOptions)
    return (
      <div className="tw:h-full tw:w-full tw:animate-pulse tw:rounded-(--embed-radius,1rem) tw:bg-gray-100 tw:motion-reduce:animate-none" />
    )

  const marker = ({ event, position }: MapPin, clusterer?: ComponentProps<typeof MarkerF>['clusterer']) => {
    const raised = event.id === highlightedId || event.id === selectedId
    return (
      <MarkerF
        key={event.id}
        clusterer={clusterer}
        position={position}
        title={event.name}
        icon={pinIcon({ pinColor, raised })}
        zIndex={raised ? Number(google.maps.Marker.MAX_ZINDEX) : undefined}
        options={{ optimized: false }}
        onClick={() => {
          setTappedId(event.id)
          onSelect(event.id)
        }}
      />
    )
  }

  return (
    <div
      role="region"
      aria-label={t('map.label')}
      className="tw:h-full tw:w-full tw:overflow-hidden tw:rounded-(--embed-radius,1rem)"
    >
      <GoogleMap
        mapContainerClassName="tw:h-full tw:w-full"
        options={phone ? PHONE_MAP_OPTIONS : MAP_OPTIONS}
        onLoad={setMap}
      >
        <GoogleMarkerClusterer options={clustererOptions}>
          {(clusterer) => <>{pins.filter((pin) => pin !== selected).map((pin) => marker(pin, clusterer))}</>}
        </GoogleMarkerClusterer>
        {selected && marker(selected)}
        {selected && !phone && (
          <InfoWindowF
            position={selected.position}
            options={{
              pixelOffset: new google.maps.Size(0, -RAISED_PIN_HEIGHT),
              maxWidth: 280,
              headerContent: popupHeader,
            }}
            onCloseClick={() => onSelect(null)}
          >
            <EventSummary
              event={selected.event}
              links={links}
              rsvpLabel={rsvpLabel}
              showCoverage={showCoverage}
              locale={locale}
              titleIn={popupHeader}
            />
          </InfoWindowF>
        )}
        {selected && phone && selected.event.id === tappedId && (
          <OverlayViewF
            key={selected.event.id}
            position={selected.position}
            mapPaneName={FLOAT_PANE}
            getPixelPositionOffset={(width, height) => ({
              x: -width / 2,
              y: -height - PIN_PATH_HEIGHT * RAISED_PIN_SCALE - CALLOUT_GAP,
            })}
          >
            <PinCallout
              ref={settleCallout}
              event={selected.event}
              links={links}
              rsvpLabel={rsvpLabel}
              showCoverage={showCoverage}
              locale={locale}
              onClose={() => onSelect(null)}
            />
          </OverlayViewF>
        )}
      </GoogleMap>
    </div>
  )
}
